import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Spinner} from "react-bootstrap";
import notifier from "simple-react-notifications2";

import {useGetLeasePackageCatalogs} from "../../api/catalogs/useLeasePackageCatalogs";
import {useGetTenantLeasePackageCatalogs} from "../../api/tenants/useTenantLeasePackageCatalogs";
import {useReplaceTenantLeasePackageCatalogs} from "../../api/tenants/useReplaceTenantLeasePackageCatalogs";
import {LeasePackageCatalogSummaryDto} from "../../model/LeasePackageCatalog";
import {getRemovedIds, haveSameIds, normalizeIds} from "../../util/idSet";
import CheckboxMultiSelect, {CheckboxMultiSelectOption} from "../form/CheckboxMultiSelect";
import ConfirmModal from "../modals/ConfirmModal";

interface TenantCatalogAssignmentsProps {
    tenantId: number;
    tenantSavePending: boolean;
    onPendingChange: (pending: boolean) => void;
}

export default function TenantCatalogAssignments({
    tenantId,
    tenantSavePending,
    onPendingChange,
}: TenantCatalogAssignmentsProps) {
    const {
        loading: loadingCatalogs,
        data: catalogs,
        error: catalogsError,
        execute: executeCatalogs,
    } = useGetLeasePackageCatalogs();
    const {
        loading: loadingAssigned,
        error: assignedError,
        execute: executeAssigned,
    } = useGetTenantLeasePackageCatalogs();
    const {execute: executeReplace} = useReplaceTenantLeasePackageCatalogs();
    const [canonicalCatalogs, setCanonicalCatalogs] = useState<LeasePackageCatalogSummaryDto[]>([]);
    const [canonicalCatalogIds, setCanonicalCatalogIds] = useState<number[]>([]);
    const [selectedCatalogIds, setSelectedCatalogIds] = useState<number[]>([]);
    const [assignmentPending, setAssignmentPending] = useState(false);
    const [assignmentError, setAssignmentError] = useState<string>();
    const [assignmentInfo, setAssignmentInfo] = useState<string>();
    const [assignmentUncertain, setAssignmentUncertain] = useState(false);
    const [showRemovalConfirmation, setShowRemovalConfirmation] = useState(false);
    const [pendingCatalogIds, setPendingCatalogIds] = useState<number[]>();
    const mounted = useRef(true);
    const latestAssignmentRead = useRef(0);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            latestAssignmentRead.current += 1;
            onPendingChange(false);
        };
    }, [onPendingChange]);

    useEffect(() => {
        setCanonicalCatalogs([]);
        setCanonicalCatalogIds([]);
        setSelectedCatalogIds([]);
        setAssignmentError(undefined);
        setAssignmentInfo(undefined);
        setAssignmentUncertain(false);
        loadAssigned(false).catch(() => undefined);
        // The tenant ID is the reload boundary; hook execute identities are unstable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId]);

    async function loadAssigned(preserveIntendedSelection: boolean) {
        const request = ++latestAssignmentRead.current;
        const assignedCatalogs = await executeAssigned(tenantId);
        if(!mounted.current || request !== latestAssignmentRead.current) return assignedCatalogs;
        const assignedIds = normalizeIds(assignedCatalogs.map(catalog => catalog.id));
        setCanonicalCatalogs(assignedCatalogs);
        setCanonicalCatalogIds(assignedIds);
        if(!preserveIntendedSelection) setSelectedCatalogIds(assignedIds);
        setAssignmentUncertain(false);
        return assignedCatalogs;
    }

    function getCatalogOptions(): CheckboxMultiSelectOption[] {
        const catalogOptions = new Map<number, LeasePackageCatalogSummaryDto>();
        catalogs?.forEach(catalog => catalogOptions.set(catalog.id, catalog));
        canonicalCatalogs.forEach(catalog => catalogOptions.set(catalog.id, catalog));
        return Array.from(catalogOptions.values())
            .sort((left, right) => left.name.localeCompare(right.name) || left.id - right.id)
            .map(catalog => ({
                id: catalog.id,
                label: `${catalog.name} (ID ${catalog.id})${catalog.active ? "" : " - inactive"}`,
            }));
    }

    function toggleCatalog(id: number) {
        setSelectedCatalogIds(current => normalizeIds(
            current.includes(id) ? current.filter(selectedId => selectedId !== id) : [...current, id],
        ));
    }

    async function reconcileAssignment(intendedIds: number[]) {
        const request = ++latestAssignmentRead.current;
        try {
            const refreshedCatalogs = await executeAssigned(tenantId);
            if(!mounted.current || request !== latestAssignmentRead.current) return;
            const refreshedIds = normalizeIds(refreshedCatalogs.map(catalog => catalog.id));
            setCanonicalCatalogs(refreshedCatalogs);
            setCanonicalCatalogIds(refreshedIds);
            setSelectedCatalogIds(intendedIds);
            const assignmentMatches = haveSameIds(refreshedIds, intendedIds);
            setAssignmentUncertain(!assignmentMatches);
            if(assignmentMatches) {
                setAssignmentError(undefined);
                setAssignmentInfo("Refreshed server data matches the intended catalog selection.");
            } else {
                setAssignmentError("The save result is still unclear. Server data was refreshed and your intended selection was preserved. Retry the save or refetch before editing again.");
            }
        } catch (e) {
            if(!mounted.current || request !== latestAssignmentRead.current) return;
            setSelectedCatalogIds(intendedIds);
            setAssignmentUncertain(true);
            setAssignmentError("The save and the follow-up refresh both failed. Retry the save or refetch before editing again.");
        }
    }

    async function saveCatalogAssignment(intendedIds: number[]) {
        latestAssignmentRead.current += 1;
        setAssignmentPending(true);
        onPendingChange(true);
        setAssignmentError(undefined);
        setAssignmentInfo(undefined);
        try {
            const assignedCatalogs = await executeReplace(tenantId, {leasePackageCatalogIds: intendedIds});
            if(!mounted.current) return;
            const assignedIds = normalizeIds(assignedCatalogs.map(catalog => catalog.id));
            setCanonicalCatalogs(assignedCatalogs);
            setCanonicalCatalogIds(assignedIds);
            setSelectedCatalogIds(assignedIds);
            setAssignmentUncertain(false);
            notifier.success("Catalog assignments saved");
        } catch (e) {
            if(!mounted.current) return;
            await reconcileAssignment(intendedIds);
        } finally {
            if(!mounted.current) return;
            setAssignmentPending(false);
            onPendingChange(false);
            setShowRemovalConfirmation(false);
            setPendingCatalogIds(undefined);
        }
    }

    async function retryRefetch() {
        setAssignmentPending(true);
        onPendingChange(true);
        try {
            await reconcileAssignment(normalizeIds(selectedCatalogIds));
        } finally {
            setAssignmentPending(false);
            onPendingChange(false);
        }
    }

    function requestCatalogAssignmentSave() {
        const intendedIds = normalizeIds(selectedCatalogIds);
        if(getRemovedIds(canonicalCatalogIds, intendedIds).length > 0) {
            setPendingCatalogIds(intendedIds);
            setShowRemovalConfirmation(true);
        } else {
            saveCatalogAssignment(intendedIds).catch(() => undefined);
        }
    }

    function getRemovedCatalogs(): LeasePackageCatalogSummaryDto[] {
        const removedIds = new Set(getRemovedIds(canonicalCatalogIds, pendingCatalogIds || selectedCatalogIds));
        return canonicalCatalogs.filter(catalog => removedIds.has(catalog.id));
    }

    const assignmentDirty = !haveSameIds(canonicalCatalogIds, selectedCatalogIds);
    const controlsDisabled = assignmentPending || tenantSavePending || loadingCatalogs
        || !!catalogsError || assignmentUncertain;

    return (
        <section className="mt-5 mb-5" aria-labelledby="tenant-catalogs-heading">
            <h2 id="tenant-catalogs-heading" className="h4">Catalog Assignments</h2>
            {catalogsError && (
                <Alert variant="danger" role="alert">
                    <p>Available catalogs could not be loaded.</p>
                    <Button
                        variant="outline-danger"
                        onClick={() => executeCatalogs().catch(() => undefined)}
                        disabled={loadingCatalogs}
                    >
                        Retry
                    </Button>
                </Alert>
            )}
            {assignedError && canonicalCatalogs.length === 0 && (
                <Alert variant="danger" role="alert">
                    <p>Current catalog assignments could not be loaded.</p>
                    <Button
                        variant="outline-danger"
                        onClick={() => loadAssigned(false).catch(() => undefined)}
                        disabled={loadingAssigned}
                    >
                        Retry
                    </Button>
                </Alert>
            )}
            {assignmentError && <Alert variant="danger" role="alert">{assignmentError}</Alert>}
            {assignmentInfo && <Alert variant="info" role="status">{assignmentInfo}</Alert>}
            {(loadingAssigned && canonicalCatalogs.length === 0) || (loadingCatalogs && catalogs === undefined) ? (
                <p aria-live="polite"><Spinner animation="border" size="sm" className="me-2" />Loading catalogs and assignments...</p>
            ) : (
                <CheckboxMultiSelect
                    idPrefix="tenant-catalog"
                    legend="Catalogs available to this tenant"
                    options={getCatalogOptions()}
                    selectedIds={selectedCatalogIds}
                    onToggle={toggleCatalog}
                    disabled={controlsDisabled || (!!assignedError && canonicalCatalogs.length === 0)}
                    emptyMessage="No catalogs are available."
                />
            )}
            <Button
                className="mt-3"
                variant="primary"
                onClick={requestCatalogAssignmentSave}
                disabled={controlsDisabled || loadingAssigned || !assignmentDirty}
            >
                {assignmentPending && <Spinner animation="border" size="sm" className="me-2" />}
                Save Catalog Assignments
            </Button>
            {assignmentUncertain && (
                <>
                    <Button
                        className="mt-3 ms-2"
                        variant="outline-primary"
                        onClick={() => saveCatalogAssignment(normalizeIds(selectedCatalogIds)).catch(() => undefined)}
                        disabled={assignmentPending || tenantSavePending}
                    >
                        Retry Save
                    </Button>
                    <Button
                        className="mt-3 ms-2"
                        variant="outline-primary"
                        onClick={() => retryRefetch().catch(() => undefined)}
                        disabled={assignmentPending || tenantSavePending}
                    >
                        Retry Refetch
                    </Button>
                </>
            )}

            <ConfirmModal
                show={showRemovalConfirmation}
                pending={assignmentPending}
                onHide={() => setShowRemovalConfirmation(false)}
                onConfirm={() => {
                    if(pendingCatalogIds) saveCatalogAssignment(pendingCatalogIds).catch(() => undefined);
                }}
                title="Remove Catalog Access?"
                cancelLabel="Cancel"
                confirmLabel="Confirm"
                body={(
                    <>
                        <p>The tenant will immediately lose offers from these catalogs:</p>
                        <ul>
                            {getRemovedCatalogs().map(catalog => (
                                <li key={catalog.id}>{catalog.name} (ID {catalog.id})</li>
                            ))}
                        </ul>
                        <p>An unsigned order may be blocked at its next existing breaking-change check.</p>
                    </>
                )}
            />
        </section>
    );
}
